import type React from 'react';
import { useTranslation } from 'react-i18next';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ActionArea } from '../../controlCenterMeta';

type CatalogSectionProps = {
    area: ActionArea;
    count: number;
    children: React.ReactNode;
};

/**
 * One area of the catalog, in its **desktop** shape: a quiet uppercase rule and
 * a three-column grid that is always open.
 *
 * Deliberately not a disclosure. Every area fits on a desktop, so a toggle here
 * would be a control whose only use is to hide things — and the compact form
 * (`CatalogAccordionItem`) exists precisely because that trade only pays below
 * `md`.
 */
export const CatalogSection: React.FC<CatalogSectionProps> = ({
    area,
    count,
    children,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex flex-col gap-2"
            {...getNodeTestIdProp(`control-center-catalog-area-${area}`)}
        >
            <h3 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.06em] text-muted">
                {t(`controlCenter.areas.${area}`)}
                <span className="font-medium normal-case tracking-normal">
                    {count}
                </span>
                <span aria-hidden className="h-px flex-1 bg-separator" />
            </h3>
            <ul
                className="grid gap-x-3 gap-y-0.5 sm:grid-cols-2 xl:grid-cols-3"
                {...getNodeTestIdProp(`control-center-catalog-list-${area}`)}
            >
                {children}
            </ul>
        </div>
    );
};
